// web/src/screens/TablesScreen.jsx
import { useState, useEffect, useRef } from 'react';
import { Tables } from '../db';
import { apiClient } from '../api';
import { useNav, useAuth } from '../App';
import SyncBadge from '../components/SyncBadge';

const STATUS_COLOR = {
  free:     'var(--table-free)',
  active:   'var(--table-active)',
  bill:     'var(--table-bill)',
  reserved: 'var(--table-reserved)',
};

function toggleTheme() {
  const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  document.querySelector('meta[name="theme-color"]').setAttribute('content', next === 'dark' ? '#1c1c1c' : '#faf9f6');
  localStorage.setItem('serveiq-theme', next);
}

export default function TablesScreen() {
  const [tables, setTables]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [workPeriod, setWorkPeriod] = useState(undefined); // undefined = not loaded yet
  const { navigate, isOnline }  = useNav();
  const { user, logout, permissions } = useAuth();
  const pollRef                 = useRef(null);
  const [theme, setTheme]       = useState(
    document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
  );

  const handleThemeToggle = () => {
    toggleTheme();
    setTheme(document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');
  };

  const loadWorkPeriod = async () => {
    try {
      const { data } = await apiClient.get('/workperiods/current');
      setWorkPeriod(data);
    } catch {
      setWorkPeriod(null);
    }
  };

  const handleStartPeriod = async () => {
    try {
      await apiClient.post('/workperiods/start', {});
      await loadWorkPeriod();
    } catch (err) {
      alert(err.data?.error || 'Failed to start work period');
    }
  };

  const handleClosePeriod = async () => {
    if (!workPeriod?.id) return;
    try {
      await apiClient.post(`/workperiods/${workPeriod.id}/close`, {});
      await loadWorkPeriod();
    } catch (err) {
      alert(err.data?.error || 'Failed to close work period');
    }
  };

  // Load tables on mount
  useEffect(() => {
    loadTables();
    loadWorkPeriod();

    // Refresh when tab/window regains focus (catches returning from payment)
    const onFocus = () => loadTables();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) loadTables();
    });

    return () => {
      window.removeEventListener('focus', onFocus);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Poll every 5 seconds when any table is active (waiting for STK callback)
  useEffect(() => {
    const hasActive = tables.some(t => ['active','sent','bill'].includes(t.status));
    if (hasActive) {
      if (!pollRef.current) {
        pollRef.current = setInterval(loadTables, 5000);
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [tables]);

  const loadTables = async () => {
    try {
      const { data } = await apiClient.get('/tables');
      await Tables.upsertMany(data);
      setTables(data);
    } catch {
      const local = await Tables.getAll();
      setTables(local);
    } finally {
      setLoading(false);
    }
  };

  const handleTableTap = async (table) => {
    if (table.status === 'free') {
      try {
        const { data: ticket } = await apiClient.post('/tickets', { table_id: table.id });
        await Tables.setStatus(table.id, 'active');
        setTables(prev => prev.map(t =>
          t.id === table.id ? { ...t, status: 'active', ticket_id: ticket.id } : t
        ));
        navigate('order', { table, ticketId: ticket.id });
      } catch (err) {
        alert(err.data?.error || 'Failed to open table');
      }
    } else if (['active','sent','ready','bill'].includes(table.status)) {
      // Always use ticket_id from DB — never stale
      if (!table.ticket_id) {
        await loadTables();
        return;
      }
      navigate('order', { table, ticketId: table.ticket_id });
    }
  };

  if (loading) {
    return (
      <div style={s.centered}>
        <p style={{ color: 'var(--text-muted)', fontFamily: "'Barlow Condensed', sans-serif" }}>
          Loading tables...
        </p>
      </div>
    );
  }

  const freeCount   = tables.filter(t => t.status === 'free').length;
  const activeCount = tables.filter(t => t.status !== 'free').length;

  return (
    <div style={s.container}>
      {/* Header — Row 1: title + Sign Out, never wrapped/hidden regardless
          of how much status/nav content Row 2 accumulates below */}
      <div style={s.header}>
        <div style={s.headerTitleGroup}>
          <span style={s.headerTitle}>TABLES</span>
          <span style={s.headerSub}> · {user?.venue_name || 'ServeIQ'}</span>
        </div>
        <button style={s.signOutBtn} onClick={logout}>SIGN OUT</button>
      </div>

      {/* Header — Row 2: everything else, in a horizontally scrollable
          strip so a new item here scrolls rather than silently clipping */}
      <div style={s.headerRow2}>
        {workPeriod !== undefined && (
          <div style={s.periodIndicator}>
            <span style={{ color: workPeriod ? 'var(--success)' : 'var(--danger)' }}>
              ● PERIOD {workPeriod ? 'OPEN' : 'CLOSED'}
            </span>
            {permissions?.can_start_workperiod && (
              workPeriod
                ? <button style={s.periodBtn} onClick={handleClosePeriod}>CLOSE</button>
                : <button style={s.periodBtn} onClick={handleStartPeriod}>START</button>
            )}
          </div>
        )}
        <SyncBadge isOnline={isOnline} inline />
        {permissions?.can_open_dashboard && (
          <button style={s.headerBtn} onClick={() => navigate('dashboard')}>DASHBOARD</button>
        )}
        <button style={s.headerBtn} onClick={() => navigate('myorders')}>MY ORDERS</button>
        <button style={s.headerBtn} onClick={handleThemeToggle} aria-label="Toggle theme">
          <i className={`ti ${theme === 'dark' ? 'ti-sun' : 'ti-moon'}`} aria-hidden="true" />
        </button>
      </div>

      {/* Stats bar */}
      <div style={s.statsBar}>
        <span style={{ color: 'var(--success)', fontSize: 12, fontWeight: 700 }}>
          ● {freeCount} FREE
        </span>
        <span style={{ color: 'var(--table-active)', fontSize: 12, fontWeight: 700, marginLeft: 16 }}>
          ● {activeCount} ACTIVE
        </span>
        {pollRef.current && (
          <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 16 }}>
            auto-refreshing...
          </span>
        )}
      </div>

      {/* Legend */}
      <div style={s.legend}>
        {Object.entries(STATUS_COLOR).map(([status, color]) => (
          <div key={status} style={s.legendItem}>
            <div style={{ ...s.legendDot, backgroundColor: color }} />
            <span style={s.legendLabel}>{status.toUpperCase()}</span>
          </div>
        ))}
      </div>

      {/* Tables grid */}
      <div style={s.grid}>
        {tables.map(table => {
          const isPartial = table.ticket_balance != null
            && Number(table.ticket_balance) > 0
            && Number(table.ticket_balance) < Number(table.ticket_total);

          return (
            <button
              key={table.id}
              style={{
                ...s.tile,
                backgroundColor: STATUS_COLOR[table.status] || STATUS_COLOR.free,
              }}
              onClick={() => handleTableTap(table)}
            >
              {isPartial && <span style={s.partialBadge}>PARTIAL</span>}
              <span style={s.tileName}>{table.name}</span>
              {table.ticket_total > 0 && (
                <>
                  {isPartial && (
                    <span style={s.tileWasTotal}>
                      was KES {Number(table.ticket_total).toLocaleString()}
                    </span>
                  )}
                  <span style={s.tileTotal}>
                    {isPartial
                      ? `KES ${Number(table.ticket_balance).toLocaleString()} owed`
                      : `KES ${Number(table.ticket_total).toLocaleString()}`}
                  </span>
                </>
              )}
              {table.waiter_name && (
                <span style={s.tileWaiter}>{table.waiter_name}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Refresh button */}
      <button style={s.refreshBtn} onClick={loadTables}>↻ REFRESH</button>
    </div>
  );
}

const s = {
  container:   { backgroundColor: 'var(--bg)', minHeight: '100vh', paddingBottom: 80,
                  fontFamily: "'Barlow Condensed', sans-serif" },
  centered:    { display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' },
  header:      { display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'12px 16px', borderBottom:'1px solid var(--border)', flexShrink:0 },
  headerTitleGroup: { minWidth:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  headerTitle: { color:'var(--orange)', fontSize:20, fontWeight:700, letterSpacing:2 },
  headerSub:   { color:'var(--text-muted)', fontSize:14 },
  headerRow2:  { display:'flex', gap:8, alignItems:'center', overflowX:'auto',
                  padding:'8px 16px', borderBottom:'1px solid var(--border)', flexShrink:0 },
  headerBtn:   { background:'none', border:'none', color:'var(--orange)', fontSize:13,
                  fontWeight:700, cursor:'pointer', letterSpacing:1, fontFamily:'inherit',
                  flexShrink:0, whiteSpace:'nowrap' },
  periodIndicator: { display:'flex', alignItems:'center', gap:8, fontSize:11, fontWeight:700,
                  letterSpacing:1, borderRight:'1px solid var(--border)', paddingRight:12,
                  flexShrink:0, whiteSpace:'nowrap' },
  periodBtn:   { padding:'4px 10px', background:'var(--surface-2)', border:'1px solid var(--border)',
                  borderRadius:6, color:'var(--text)', fontSize:11, fontWeight:700, cursor:'pointer',
                  letterSpacing:1, fontFamily:'inherit', flexShrink:0, whiteSpace:'nowrap' },
  signOutBtn:  { background:'none', border:'none', color:'var(--danger)', fontSize:13,
                  fontWeight:700, cursor:'pointer', letterSpacing:1, fontFamily:'inherit',
                  flexShrink:0, whiteSpace:'nowrap' },
  statsBar:    { padding:'6px 16px', background:'var(--surface-2)', borderBottom:'1px solid var(--border)' },
  legend:      { display:'flex', gap:16, padding:'8px 16px', borderBottom:'1px solid var(--border)' },
  legendItem:  { display:'flex', alignItems:'center', gap:6 },
  legendDot:   { width:10, height:10, borderRadius:'50%' },
  legendLabel: { color:'var(--text-muted)', fontSize:11, letterSpacing:1 },
  grid:        { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, padding:12 },
  tile:        { position:'relative', border:'none', borderRadius:8, padding:'16px 8px', cursor:'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center',
                  gap:4, minHeight:80, transition:'background 0.3s', fontFamily:'inherit' },
  tileName:    { color:'#fff', fontSize:16, fontWeight:700, letterSpacing:1 },
  tileTotal:   { color:'rgba(255,255,255,0.9)', fontSize:12, fontWeight:600 },
  tileWasTotal:{ color:'rgba(255,255,255,0.6)', fontSize:10, textDecoration:'line-through' },
  tileWaiter:  { color:'rgba(255,255,255,0.6)', fontSize:10 },
  partialBadge:{ position:'absolute', top:4, right:4, background:'#fac775', color:'#412402',
                  fontSize:9, fontWeight:700, letterSpacing:0.5, padding:'2px 5px', borderRadius:4 },
  refreshBtn:  { position:'fixed', bottom:16, left:16, background:'var(--surface)',
                  border:'1px solid var(--border)', color:'var(--orange)', padding:'10px 20px',
                  borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer',
                  letterSpacing:1, fontFamily:'inherit' },
};
